<template>
  <div>
    <b-button
      :id="modalId + '-button'"
      @click="toggleModal"
      :variant="'outline-' + iconColor"
      class="d-flex align-items-center justify-content-center"
      :class="border ? '' : 'border-0'"
    >
      <span class="pr-1" v-if="withLabel">{{ action }}</span>
      <i id="trashbin" class="icon trash m-0" :class="'text-' + iconColor" />
    </b-button>
    <ConfirmationBox
      @confirmed="deleteObject"
      @canceled="toggleModal"
      :id="modalId"
      :isShown="isShown"
      :action="action"
    />
    <b-toast
      :id="computedToastId"
      toast-class="bg-danger text-white text-center"
      :no-close-button="true"
    >
      <template v-slot:default>
        <i class="exclamation circle icon mr-3"></i>
        <span>{{ "errors.failure" | i18n }}</span>
      </template>
    </b-toast>
  </div>
</template>
<script>
import ConfirmationBox from "../modals/ConfirmationBox.vue";
/**
 * @description Bouton permettant d'afficher une boîte modale pour supprimer le serveur
 * @author Francine Jin
 * @author Alexandre Gallant
 * @vue-prop {Function} deleteFunction - Fonction de suppression
 * @vue-prop {String} modalId - Identifiant de la boîte modale
 * @vue-prop {Number} objectId - Identifiant de l'objet à supprimer
 * @vue-prop {String} redirection - Chemin relatif où l'utilisateur sera redirigé après la suppression
 * @vue-prop {Boolean} reloadOnSuccess - Recharger la même page si la suppression a réussi
 * @vue-prop {String} iconColor - Couleur de l'icône
 * @vue-prop {Boolean} withLabel -  Si du texte doit être affiché en plus de l'icône
 * @vue-prop {String} action - Nom de l'action à éxecuter
 * @vue-prop {Boolean} border - Avec bordure ou non
 * @vue-data {Boolean} isShown - Indique si la boîte modale doit s'afficher
 * @vue-computed {String} computedToastId - L'id du toast
 */
export default {
  name: "GenericFormDelete",
  components: {
    ConfirmationBox
  },
  props: {
    deleteFunction: {
      type: Function,
      required: true
    },
    modalId: {
      type: String,
      required: true
    },
    objectId: {
      type: Number,
      required: true
    },
    redirection: {
      type: String,
      required: false,
      default: "/"
    },
    reloadOnSuccess: {
      type: Boolean,
      required: false,
      default: false
    },
    iconColor: {
      type: String,
      required: false,
      default: "primary"
    },
    withLabel: {
      type: Boolean,
      required: false,
      default: false
    },
    action: {
      type: String,
      required: false,
      default: "Supprimer"
    },
    border: {
      type: Boolean,
      required: false,
      default: true
    }
  },
  data() {
    return { isShown: false };
  },
  computed: {
    computedToastId() {
      return "error-toast-delete-" + this.modalId;
    }
  },
  methods: {
    /**
     * @description Fonction qui permet de passer à true/false le booléen indiquant si la boîte modale doit s'afficher ou non
     */
    toggleModal() {
      this.isShown = !this.isShown;
    },
    /**
     * @description Fonction permettant la suppression de l'objet
     */
    async deleteObject() {
      let result;
      await this.deleteFunction(this.objectId)
        .then(() => {
          result = true;
        })
        .catch(() => {
          this.toggleModal();
          this.$bvToast.show(this.computedToastId);
          result = false;
        });
      if (!result) {
        return;
      }
      try {
        if (this.reloadOnSuccess) {
          this.$router.go();
        } else {
          await this.$router.push(this.redirection);
        }
      } catch (e) {
        if (e != undefined) {
          this.toggleModal();
          this.$bvToast.show(this.computedToastId);
        }
      }
    }
  }
};
</script>
<style lang="scss" scoped>
button:hover i {
  color: $white !important;
}
#trashbin {
  padding-bottom: 25px !important;
}
</style>
